import { registerSW } from "virtual:pwa-register";

import { VueQueryPlugin } from "@tanstack/vue-query";
import { createApp } from "vue";
import { routerChecked } from "./router";
import "./global.scss";
import "@innoclouds/ui/tailwind.css";
import { axiosInstance, setConfig } from "@innoclouds/api";
import {
	createI18n,
	isMockMode,
	isPreviewMode,
	loadLocalFonts,
	preloadLazyRoutes,
	preventZoom,
	useAuthInterceptor,
	useLanguageInterceptor,
	useOrgUnitInterceptor,
} from "@innoclouds/shared";
import * as Sentry from "@sentry/vue";
import dayjs from "dayjs";
import i18next from "i18next";
import { createPinia } from "pinia";
import VueCookies from "vue-cookies";
import App from "./App.vue";
import router from "./router";
import { initErrorHandlers } from "./utils/useErrorHandler";
import "dayjs/locale/ru";
import "dayjs/locale/en";
import "dayjs/locale/es";
import "dayjs/locale/ar";
import "dayjs/locale/zh-cn";
import "dayjs/locale/hy-am";
import "dayjs/locale/kk";
import "dayjs/locale/uz";
import { bridge } from "@innoclouds/sdk";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

if ("serviceWorker" in navigator && !isPreviewMode && !isMockMode) {
	registerSW();
}

setConfig({ baseURL: import.meta?.env?.MODE !== "https" ? import.meta.env.VITE_API_URL : "" });
useLanguageInterceptor(axiosInstance);
useAuthInterceptor(axiosInstance);
useOrgUnitInterceptor(axiosInstance);

bridge.emit("ready", { app: "QR-MENU", version: __APP_VERSION__ });

const DAYJS_LOCALE_MAP: Record<string, string> = {
	zh: "zh-cn",
	hy: "hy-am",
};

const applyDayjsLocale = (lng: string) => {
	const normalized = (lng || "ru").split("-")[0];
	dayjs.locale(DAYJS_LOCALE_MAP[normalized] || normalized);
};

applyDayjsLocale(i18next.language);
i18next.on("languageChanged", applyDayjsLocale);

preventZoom();

const app = createI18n(createApp(App)).use(VueQueryPlugin);

Sentry.init({
	app,
	enabled: !isPreviewMode && !isMockMode,
	dsn: "https://1cf91f92f21f5f9b60fbf1fb81739aa8@o4511059808747520.ingest.de.sentry.io/4511059810975824",
	release: `inno-clouds-qr@${__APP_VERSION__}`,
	sendDefaultPii: true,
	integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
	tracesSampleRate: 0.2,
	replaysSessionSampleRate: 0,
	replaysOnErrorSampleRate: 1.0,
});

initErrorHandlers(app);

const pinia = createPinia();
app.use(router);
preloadLazyRoutes(router);
app.use(pinia);

router.beforeEach(async (to) => {
	return await routerChecked(to, app);
});
app.use(VueCookies);

const mountApp = () => app.mount("#app");

loadLocalFonts().then(mountApp, mountApp);
